/* ==========================================================================
   WOHLGANG PDP

   - native bundle selector (1 / 2 / 3 Paar), no app
   - colour swatches + size select, one set per pair, state carries on switch
   - live total, struck reference price, shown-discount badge
   - single /cart/add.js call, identical variants merged into one line
   - gallery (thumbs, arrows, swipe), sticky mobile ATC that actually buys

   PRICING — flat amount off, not a percentage
   ---------------------------------------------
   data-discount  the € amount (in cents) off tier 2/3's bundle subtotal.
                  Must match a Shopify automatic discount ("amount off
                  order", min qty 3 / min qty 5) or the cart will disagree.
                  Tier 1 carries 0 — always full price.
   data-badge     the shown discount %. A chosen, rounded marketing number
                  (35/60/70), not derived — see the section comment in
                  wg-hero.liquid for why a derived number can't land on
                  both a clean total AND a clean percentage at once.
   data-ref-unit  the fixed, display-only per-pair reference price (root),
                  used with qty to draw the struck-through price.

   No per-unit rounding anywhere: total is always base×qty − discount, to
   the cent, so the page and a correctly configured cart always agree.
   Never divide a total and round afterwards — that is where the old build
   drifted three cents away from the cart.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-wg-buy]');

  /* ---------- money ---------- */

  var MONEY_FORMAT = (root && root.dataset.moneyFormat) || '{{amount_with_comma_separator}} €';

  function withDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) return '0';
    var n = (cents / 100.0).toFixed(precision);
    var parts = n.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function money(cents) {
    var match = MONEY_FORMAT.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return MONEY_FORMAT;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.'); break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return MONEY_FORMAT.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ---------- in-page scroll ---------- */

  function headerOffset() {
    var header = document.querySelector('header-component, .header-wrapper, #header-group');
    var h = header ? header.getBoundingClientRect().height : 0;
    return h + 16;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-wg-scroll]');
    if (!link) return;
    var hash = link.getAttribute('href') || '';
    if (hash.charAt(0) !== '#' || hash.length < 2) return;

    /* Always take over navigation here — never fall through to the browser's
       native hash jump. That would leave "#hash" sitting in the address bar,
       and every later reload would re-trigger the jump from the URL itself
       (independently of this handler) instead of scrolling only on click. */
    event.preventDefault();

    var target = document.querySelector(hash);
    if (!target) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.pageYOffset - headerOffset(),
      behavior: reduce ? 'auto' : 'smooth'
    });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  /* ---------- gallery ---------- */

  document.querySelectorAll('[data-wg-gallery]').forEach(function (gallery) {
    var slides = gallery.querySelectorAll('[data-wg-slide]');
    var thumbs = gallery.querySelectorAll('[data-wg-thumb]');
    if (slides.length < 2) return;

    var index = 0;
    var thumbRail = gallery.querySelector('.wg-gallery__thumbs');

    /* Keep the active thumb in view WITHOUT ever moving the page. The old
       t.scrollIntoView({block:'nearest'}) walked up to the document scroller
       and yanked the whole page toward the gallery whenever a colour change
       swapped the featured image — very visible on mobile. This only ever
       scrolls the thumb rail itself, along its own scroll axis. */
    function revealThumb(t) {
      if (!thumbRail) return;
      var horizontal = thumbRail.scrollWidth > thumbRail.clientWidth + 1;
      var vertical = thumbRail.scrollHeight > thumbRail.clientHeight + 1;
      if (horizontal) {
        thumbRail.scrollTo({
          left: t.offsetLeft - (thumbRail.clientWidth - t.clientWidth) / 2,
          behavior: 'smooth'
        });
      } else if (vertical) {
        thumbRail.scrollTo({
          top: t.offsetTop - (thumbRail.clientHeight - t.clientHeight) / 2,
          behavior: 'smooth'
        });
      }
    }

    function show(next, opts) {
      opts = opts || {};
      index = (next + slides.length) % slides.length;
      slides.forEach(function (s) {
        s.classList.toggle('is-active', Number(s.dataset.wgSlide) === index);
      });
      thumbs.forEach(function (t) {
        var on = Number(t.dataset.wgThumb) === index;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on && opts.revealThumb !== false) revealThumb(t);
      });
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { show(Number(t.dataset.wgThumb)); });
    });

    var prev = gallery.querySelector('[data-wg-prev]');
    var next = gallery.querySelector('[data-wg-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    /* swipe */
    var startX = null;
    var stage = gallery.querySelector('.wg-gallery__stage');
    if (stage) {
      stage.addEventListener('touchstart', function (e) {
        startX = e.changedTouches[0].clientX;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
        startX = null;
      }, { passive: true });
    }

    gallery.wgShow = show;
    /* Programmatic image swap (variant change) — never scroll anything. */
    gallery.wgShowMedia = function (mediaId) {
      var slide = gallery.querySelector('[data-wg-media-id="' + mediaId + '"]');
      if (slide && Number(slide.dataset.wgSlide) !== index) {
        show(Number(slide.dataset.wgSlide), { revealThumb: false });
      }
    };
  });

  /* ---------- horizontal card rails ----------
     Native scroll-snap does the scrolling; the arrows only nudge it by one
     card, so the rail still works with a trackpad, a touch drag or the
     keyboard if the script never runs. */

  document.querySelectorAll('[data-wg-carousel]').forEach(function (rail) {
    var track   = rail.querySelector('[data-wg-rail-track]');
    var prevBtn = rail.querySelector('[data-wg-rail-prev]');
    var nextBtn = rail.querySelector('[data-wg-rail-next]');
    if (!track) return;

    /* ---------- free-scroll rail ----
       Native scroll-snap does the scrolling; the arrows only nudge it by one
       card, so the rail still works with a trackpad, a touch drag or the
       keyboard if the script never runs. */
    var items = Array.prototype.slice.call(track.children);
    var loop  = items.length >= 3;
    var unit  = 0;
    var timer = null;

    /* One copy before and one after the real set, so the rail runs forever in
       both directions. Clones are hidden from assistive tech and pulled out of
       the tab order — only the middle copy is the real content. */
    if (loop) {
      var before = document.createDocumentFragment();
      var after  = document.createDocumentFragment();
      items.forEach(function (li) {
        [before, after].forEach(function (frag) {
          var c = li.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          c.querySelectorAll('a, button, input, select').forEach(function (el) {
            el.tabIndex = -1;
          });
          frag.appendChild(c);
        });
      });
      track.appendChild(after);
      track.insertBefore(before, track.firstChild);
    }

    /* Width of exactly one copy, read off the DOM. scrollWidth / 3 would be
       wrong because the track carries padding on both ends. */
    function measure() {
      if (!loop) return;
      unit = track.children[items.length].offsetLeft - track.children[0].offsetLeft;
      if (unit && track.scrollLeft < 1) track.scrollLeft = unit;
    }

    function wrap() {
      if (!loop || !unit) return;
      if (track.scrollLeft > unit * 1.5) track.scrollLeft -= unit;
      else if (track.scrollLeft < unit * 0.5) track.scrollLeft += unit;
    }

    function step() {
      var card = track.querySelector('li');
      if (!card) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function nudge(dir) {
      track.scrollBy({
        left: dir * step(),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { nudge(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { nudge(1); });

    /* Rewrap only once the scroll has settled — moving scrollLeft mid-animation
       cancels the smooth scroll and jumps. */
    track.addEventListener('scroll', function () {
      if (!loop) return;
      clearTimeout(timer);
      timer = setTimeout(wrap, 120);
    }, { passive: true });

    window.addEventListener('resize', measure);
    if (loop) {
      measure();
    } else if (prevBtn && nextBtn) {
      var sync = function () {
        var max = track.scrollWidth - track.clientWidth - 2;
        prevBtn.disabled = track.scrollLeft <= 2;
        nextBtn.disabled = track.scrollLeft >= max;
      };
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    }
  });

  /* ---------- sliding-window card grid (testimonials) ----------
     A conveyor, not a pager: each step shows cards [index .. index+size-1]
     and index moves by 1, so two of the three visible cards are always
     held over from the previous view and exactly one leaves/enters — the
     actual behavior of a carousel, not a set of fixed pages. Still no
     transform, no width math, no scroll offset: "visible" is still just
     "cards index..index+size-1 don't have the hidden attribute, the rest
     do", the same crash-proof mechanism as before, just re-evaluated on a
     rolling window instead of a fixed page boundary. */
  /* Testimonials are a CSS overflow rail now (not a paged grid). Show every
     card, and auto-advance by scrolling the rail one card at a time,
     looping back to the start. Pauses on hover / touch / focus. */
  document.querySelectorAll('[data-wg-tstx]').forEach(function (root) {
    var rail  = root.querySelector('.wg-tstx__grid');
    var cards = rail ? Array.prototype.slice.call(rail.querySelectorAll('[data-wg-tstx-card]')) : [];
    if (!rail || cards.length === 0) return;

    cards.forEach(function (c) { c.hidden = false; });

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var timer = null;

    function step() {
      var w = cards[0].getBoundingClientRect().width + 16;
      var maxLeft = rail.scrollWidth - rail.clientWidth - 4;
      if (rail.scrollLeft >= maxLeft) {
        rail.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        rail.scrollBy({ left: w, behavior: 'smooth' });
      }
    }
    function stopAuto() { if (timer) { clearInterval(timer); timer = null; } }
    function startAuto() {
      if (timer || reduceMotion || cards.length < 2) return;
      timer = setInterval(step, 4200);
    }

    if (root.hasAttribute('data-wg-tstx-autoscroll')) {
      rail.addEventListener('pointerenter', stopAuto);
      rail.addEventListener('pointerleave', startAuto);
      rail.addEventListener('touchstart', stopAuto, { passive: true });
      rail.addEventListener('focusin', stopAuto);
      rail.addEventListener('focusout', startAuto);
      startAuto();
    }
  });

  /* ---------- mini review carousel (auto-advance + dots) ---------- */

  document.querySelectorAll('[data-wg-mini-carousel]').forEach(function (widget) {
    var track = widget.querySelector('[data-wg-mini-track]');
    var dots  = Array.prototype.slice.call(widget.querySelectorAll('[data-wg-mini-dots] button'));
    if (!track || dots.length < 2) return;

    var count  = track.children.length;
    var index  = 0;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var timer  = null;

    function paint() {
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
    }

    function show(next) {
      index = (next + count) % count;
      track.scrollTo({ left: track.clientWidth * index, behavior: reduce ? 'auto' : 'smooth' });
      paint();
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        show(parseInt(dot.dataset.i, 10));
        restart();
      });
    });

    /* A swipe should move the dots too, not just the dot clicks. */
    var scrollTimer = null;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        index = Math.round(track.scrollLeft / track.clientWidth);
        paint();
      }, 100);
    }, { passive: true });

    function stop() { if (timer) clearTimeout(timer); timer = null; }
    function start() {
      if (reduce) return; /* no auto-advance for reduced-motion */
      stop();
      timer = setTimeout(function () { show(index + 1); start(); }, 4500);
    }
    function restart() { stop(); start(); }

    ['pointerdown', 'touchstart'].forEach(function (evt) {
      track.addEventListener(evt, stop, { passive: true });
    });
    ['pointerup', 'touchend'].forEach(function (evt) {
      track.addEventListener(evt, function () { setTimeout(start, 600); }, { passive: true });
    });
    widget.addEventListener('mouseenter', stop);
    widget.addEventListener('mouseleave', start);
    window.addEventListener('resize', function () { track.scrollLeft = track.clientWidth * index; });

    start();
  });

  /* ---------- micro-testimonial carousel (top of the buy box) ---------- */

  document.querySelectorAll('[data-wg-microtest]').forEach(function (widget) {
    var track = widget.querySelector('[data-wg-microtest-track]');
    var dotsWrap = widget.querySelector('[data-wg-microtest-dots]');
    if (!track) return;

    var count = track.children.length;
    if (count < 2) { if (dotsWrap) dotsWrap.hidden = true; return; }

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var index = 0, timer = null, scrollTimer = null, dots = [];

    for (var i = 0; i < count; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'wg-microtest__dot' + (i === 0 ? ' is-active' : '');
      b.setAttribute('aria-label', 'Stimme ' + (i + 1) + ' von ' + count);
      (function (n) { b.addEventListener('click', function () { go(n); restart(); }); })(i);
      dotsWrap.appendChild(b);
      dots.push(b);
    }

    function paint() { dots.forEach(function (d, k) { d.classList.toggle('is-active', k === index); }); }
    function go(n) {
      index = (n + count) % count;
      track.scrollTo({ left: track.clientWidth * index, behavior: reduce ? 'auto' : 'smooth' });
      paint();
    }
    function stop() { if (timer) clearTimeout(timer); timer = null; }
    function start() { if (reduce) return; stop(); timer = setTimeout(function () { go(index + 1); start(); }, 5000); }
    function restart() { stop(); start(); }

    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        index = Math.round(track.scrollLeft / track.clientWidth);
        paint();
      }, 100);
    }, { passive: true });

    ['pointerdown', 'touchstart'].forEach(function (e) { track.addEventListener(e, stop, { passive: true }); });
    ['pointerup', 'touchend'].forEach(function (e) {
      track.addEventListener(e, function () { setTimeout(start, 800); }, { passive: true });
    });
    widget.addEventListener('mouseenter', stop);
    widget.addEventListener('mouseleave', start);
    window.addEventListener('resize', function () { track.scrollLeft = track.clientWidth * index; });

    start();
  });

  /* ---------- progressive size pickers (design plan §A) ----------
     "Alle Paare in gleicher Größe & Farbe" is on by default: pair 1 drives
     every pair and pairs 2…N stay hidden. The N per-pair rows always exist
     in the DOM — the cart logic reads them unchanged; this layer only
     shows/mirrors them. */

  document.querySelectorAll('[data-wg-picks]').forEach(function (picks) {
    var same       = picks.querySelector('[data-wg-same]');
    var rows       = Array.prototype.slice.call(picks.querySelectorAll('[data-wg-pair]'));
    var firstN     = rows[0] && rows[0].querySelector('.wg-pick__n');
    var firstNText = firstN ? firstN.textContent : '';

    /* Copy pair 1's colour + size onto every other pair. */
    function mirror() {
      var src = rows[0];
      if (!src) return;
      var srcControls = src.querySelectorAll('[data-wg-opt]');
      rows.slice(1).forEach(function (row) {
        var dstControls = row.querySelectorAll('[data-wg-opt]');
        Array.prototype.forEach.call(srcControls, function (sc, i) {
          var dc = dstControls[i];
          if (!dc) return;
          if (sc.tagName === 'SELECT') {
            dc.value = sc.value;
          } else {
            var picked = sc.querySelector('input:checked');
            if (!picked) return;
            Array.prototype.forEach.call(dc.querySelectorAll('input'), function (inp) {
              inp.checked = inp.value === picked.value;
            });
          }
        });
      });
    }
    picks.wgMirror = mirror;

    function applyUniform(on) {
      picks.classList.toggle('is-uniform', on);
      if (firstN) firstN.textContent = on ? 'Alle Paare' : firstNText;
      if (on) mirror();
    }
    picks.wgApplyUniform = applyUniform;

    if (same) {
      same.addEventListener('change', function () { applyUniform(same.checked); });
      applyUniform(same.checked);
    }
  });

  /* ---------- size guide modal ---------- */

  (function () {
    var modal = document.querySelector('[data-wg-sizeguide-modal]');
    if (!modal) return;
    var native = typeof modal.showModal === 'function';
    var lastFocus = null;

    function openModal() {
      lastFocus = document.activeElement;
      if (native) {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
        modal.classList.add('is-fallback-open');
        document.documentElement.style.overflow = 'hidden';
      }
      var close = modal.querySelector('[data-wg-sizeguide-close]');
      if (close) close.focus();
    }
    function closeModal() {
      if (native) {
        modal.close();
      } else {
        modal.removeAttribute('open');
        modal.classList.remove('is-fallback-open');
        document.documentElement.style.overflow = '';
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    document.querySelectorAll('[data-wg-sizeguide]').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.preventDefault(); openModal(); });
    });
    modal.querySelectorAll('[data-wg-sizeguide-close]').forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });
    /* click on the backdrop (the dialog element itself, outside the panel) */
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
    /* ESC on non-native fallback (native <dialog> already handles it) */
    if (!native) {
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && modal.classList.contains('is-fallback-open')) closeModal();
      });
    }
  })();

  /* ---------- mechanism: legend <-> dot highlight (design plan §D) ---------- */

  document.querySelectorAll('[data-wg-mech]').forEach(function (mech) {
    var entries = mech.querySelectorAll('[data-wg-entry]');
    var dots    = mech.querySelectorAll('[data-wg-dot]');
    if (!entries.length || !dots.length) return;

    function light(n, on) {
      entries.forEach(function (e) {
        if (e.dataset.wgEntry === n) e.classList.toggle('is-active', on);
      });
      dots.forEach(function (d) {
        if (d.dataset.wgDot === n) d.classList.toggle('is-active', on);
      });
    }

    entries.forEach(function (entry) {
      var n = entry.dataset.wgEntry;
      ['mouseenter', 'focus'].forEach(function (evt) {
        entry.addEventListener(evt, function () { light(n, true); });
      });
      ['mouseleave', 'blur'].forEach(function (evt) {
        entry.addEventListener(evt, function () { light(n, false); });
      });
    });
  });

  /* ---------- FAQ disclosure ---------- */

  document.querySelectorAll('[data-wg-faq-q]').forEach(function (button) {
    if (button.dataset.wgFaqBound) return;   // already wired by wg-faq's own inline script
    button.dataset.wgFaqBound = '1';
    button.addEventListener('click', function () {
      var open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', open ? 'false' : 'true');
      var panel = document.getElementById(button.getAttribute('aria-controls'));
      if (panel) panel.hidden = open;
    });
  });

  /* ---------- endless drift rails, still hand-scrollable ----------
     A real overflow-x scroller whose content is rendered twice. A rAF loop
     nudges scrollLeft a hair each frame (constant = linear); passing one set's
     width subtracts it — invisible, the halves are identical. Any manual
     scroll / drag / wheel pauses the drift; it resumes shortly after.
     `mobileOnly` gates it to <=749px (used by the proof-stats strip). */

  function wgInitDriftRail(rail, opts) {
    opts = opts || {};
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var SPEED = opts.speed || 0.5;   // px per frame
    var mq = opts.mobileOnly ? window.matchMedia('(max-width: 749px)') : null;
    var half = 0;
    var pos = rail.scrollLeft;       // our own float position — scrollLeft is
                                    // integer-quantized in some engines, so a
                                    // sub-pixel `scrollLeft +=` would never move.
    var paused = false;
    var resumeTimer = null;
    var selfScroll = false;

    function active() { return !mq || mq.matches; }
    function measure() { half = active() ? rail.scrollWidth / 2 : 0; }
    measure();
    window.addEventListener('resize', measure);
    if (mq) {
      var onChange = function () {
        if (!mq.matches) { half = 0; pos = 0; try { rail.scrollLeft = 0; } catch (e) {} }
        else { requestAnimationFrame(measure); }   // let the flex layout settle first
      };
      mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    }

    function frame() {
      if (active()) {
        if (half <= 0) measure();               // self-heal after a viewport change
        if (!paused && half > 0) {
          pos += SPEED;
          if (pos >= half) pos -= half;
          else if (pos < 0) pos += half;
          selfScroll = true;
          rail.scrollLeft = pos;
        }
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    function pause() {
      paused = true;
      if (resumeTimer) { clearTimeout(resumeTimer); resumeTimer = null; }
    }
    function resumeSoon() {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () { paused = false; }, 1500);
    }

    ['pointerdown', 'touchstart', 'wheel'].forEach(function (ev) {
      rail.addEventListener(ev, pause, { passive: true });
    });
    ['pointerup', 'pointercancel', 'touchend'].forEach(function (ev) {
      rail.addEventListener(ev, resumeSoon, { passive: true });
    });
    rail.addEventListener('pointerenter', pause);
    rail.addEventListener('pointerleave', resumeSoon);
    rail.addEventListener('focusin', pause);
    rail.addEventListener('focusout', resumeSoon);
    rail.addEventListener('scroll', function () {
      if (selfScroll) { selfScroll = false; return; }
      // manual scroll: adopt the user's position, keep it inside one set
      pos = rail.scrollLeft;
      if (half > 0) {
        if (pos >= half) { pos -= half; selfScroll = true; rail.scrollLeft = pos; }
        else if (pos < 0) { pos += half; selfScroll = true; rail.scrollLeft = pos; }
      }
    }, { passive: true });
  }

  document.querySelectorAll('[data-wg-occ-marquee]').forEach(function (rail) {
    if (rail.querySelectorAll('.wg-occ__card').length >= 4) wgInitDriftRail(rail, { speed: 0.6 });
  });
  document.querySelectorAll('[data-wg-stats-marquee]').forEach(function (rail) {
    wgInitDriftRail(rail, { mobileOnly: true, speed: 0.35 });
  });

  /* ---------- bundle + cart ---------- */

  if (!root) return;

  var variants = [];
  try {
    variants = JSON.parse(root.querySelector('[data-wg-variants]').textContent);
  } catch (e) {
    variants = [];
  }

  var form       = root.querySelector('[data-wg-form]');
  var errorBox   = root.querySelector('[data-wg-error]');
  var submitBtn  = root.querySelector('[data-wg-submit]');
  var fallbackId = root.querySelector('[data-wg-fallback-id]');
  var gallery    = document.querySelector('[data-wg-gallery]');

  var elTotal = root.querySelector('[data-wg-total]');
  var elUnit  = root.querySelector('[data-wg-unit]');
  var elRef   = root.querySelector('[data-wg-ref]');   /* removed from markup (§1.5) — guarded */
  var elOff   = root.querySelector('[data-wg-off]');   /* removed from markup (§1.5) — guarded */
  var elShip  = root.querySelector('[data-wg-ship]');

  var basePrice = parseInt(root.dataset.base, 10) || 0;
  var refUnit   = parseInt(root.dataset.refUnit, 10) || 0;
  var freeFrom  = parseInt(root.dataset.freeFrom, 10) || 2;
  var shipFree  = root.dataset.shipFree || '';
  var shipPaid  = root.dataset.shipPaid || '';

  /* Last value the shopper picked per option index, so switching tier does
     not silently reset a size they already chose. */
  var sticky = {};

  function selectedTier() {
    return root.querySelector('input[name="wg-tier"]:checked');
  }

  function pairsOf(tierInput) {
    var panel = root.querySelector('[data-wg-picks="' + tierInput.value + '"]');
    return panel ? panel.querySelectorAll('[data-wg-pair]') : [];
  }

  function controlsOf(pair) {
    return pair.querySelectorAll('[data-wg-opt]');
  }

  function readControl(control) {
    if (control.tagName === 'SELECT') return control.value;
    var checked = control.querySelector('input:checked');
    return checked ? checked.value : '';
  }

  function writeControl(control, value) {
    if (!value) return;
    if (control.tagName === 'SELECT') {
      var match = Array.prototype.find.call(control.options, function (o) {
        return o.value === value && !o.disabled;
      });
      if (match) control.value = value;
      return;
    }
    var input = Array.prototype.find.call(control.querySelectorAll('input'), function (i) {
      return i.value === value;
    });
    if (input && !input.disabled) input.checked = true;
  }

  function chosenValues(pair) {
    return Array.prototype.map.call(controlsOf(pair), readControl);
  }

  function findVariant(values) {
    if (values.some(function (v) { return !v; })) return null;
    return variants.find(function (v) {
      return values.every(function (value, i) { return v.options[i] === value; });
    }) || null;
  }

  function variantFor(pair) {
    return findVariant(chosenValues(pair));
  }

  /* Grey out sizes AND colours that cannot be sold in the current combination.
     The old build only did sizes, so a sold-out colour stayed selectable and
     failed silently at submit. */
  function markAvailability(pair) {
    var controls = controlsOf(pair);
    var chosen = chosenValues(pair);

    controls.forEach(function (control) {
      var index = parseInt(control.dataset.wgOpt, 10);

      function sellable(value) {
        var probe = chosen.slice();
        probe[index] = value;
        var match = findVariant(probe);
        return !!(match && match.available);
      }

      if (control.tagName === 'SELECT') {
        Array.prototype.forEach.call(control.options, function (option) {
          var ok = sellable(option.value);
          option.disabled = !ok;
          option.textContent = (option.dataset.label || option.value) + (ok ? '' : ' · ausverkauft');
        });
        if (control.selectedOptions[0] && control.selectedOptions[0].disabled) {
          var free = Array.prototype.find.call(control.options, function (o) { return !o.disabled; });
          if (free) control.value = free.value;
        }
      } else {
        control.querySelectorAll('input').forEach(function (input) {
          var ok = sellable(input.value);
          input.disabled = !ok;
          input.closest('.wg-swatch').classList.toggle('is-out', !ok);
        });
        var current = control.querySelector('input:checked');
        if (!current || current.disabled) {
          var open = control.querySelector('input:not([disabled])');
          if (open) open.checked = true;
        }
      }
    });

    /* Name the chosen colour — a bare dot tells a phone user nothing. */
    var label = pair.querySelector('[data-wg-optname]');
    if (label) {
      var swatches = pair.querySelector('.wg-swatches');
      var picked = swatches && swatches.querySelector('input:checked');
      label.textContent = picked ? picked.value : '';
    }
  }

  /* ---------- pricing ----------
     Tier 2/3 are a flat euro amount off the bundle subtotal, not a percentage.
     A percentage rounds per unit and can miss a clean total when qty doesn't
     divide it evenly (55,00 € / 3 has no exact per-unit answer). A flat
     amount off the subtotal has no rounding step at all — the total is
     always exactly base×qty − discount, to the cent, matching a Shopify
     "amount off order" automatic discount (min qty 3 / min qty 5) exactly. */

  function totalFor(unitBase, qty, discountCents) {
    return Math.max(0, (unitBase * qty) - discountCents);
  }

  /* Keep every tier card honest against whatever variant is actually chosen,
     in case sizes are not all priced the same. */
  function currentBase() {
    var tier = selectedTier();
    if (!tier) return basePrice;
    var first = pairsOf(tier)[0];
    var variant = first && variantFor(first);
    return variant ? variant.price : basePrice;
  }

  function repriceCards(unitBase) {
    root.querySelectorAll('input[name="wg-tier"]').forEach(function (input) {
      var qty = parseInt(input.dataset.qty, 10);
      var discount = parseInt(input.dataset.discount, 10) || 0;
      var total = totalFor(unitBase, qty, discount);
      var unit = Math.round(total / qty);

      var card = input.nextElementSibling;
      var unitText = card.querySelector('[data-wg-card-unit]');
      if (unitText) unitText.textContent = money(unit) + '/Paar';
      var totalText = card.querySelector('[data-wg-card-total]');
      if (totalText) totalText.textContent = money(total);
    });
  }

  function refresh() {
    var tier = selectedTier();
    if (!tier) return;

    var pairs = pairsOf(tier);
    pairs.forEach(markAvailability);

    var qty = parseInt(tier.dataset.qty, 10);
    var discount = parseInt(tier.dataset.discount, 10) || 0;
    var badgePct = parseInt(tier.dataset.badge, 10) || 0;

    var unitBase = currentBase();
    repriceCards(unitBase);

    /* Sum the real chosen variants so a mixed-price order still adds up,
       then take the flat discount off the sum once — never per pair. */
    var subtotal = 0;
    pairs.forEach(function (pair) {
      var variant = variantFor(pair);
      subtotal += variant ? variant.price : unitBase;
    });
    var total = Math.max(0, subtotal - discount);
    var unit = Math.round(total / qty);
    var ref = refUnit * qty;

    if (elTotal) elTotal.textContent = money(total);
    if (elUnit) elUnit.textContent = money(unit) + ' / Paar';
    if (elRef) {
      elRef.textContent = money(ref);
      elRef.hidden = ref <= total;
    }
    if (elOff) {
      elOff.textContent = '−' + badgePct + ' %';
      elOff.hidden = badgePct <= 0;
    }
    if (elShip) elShip.textContent = qty >= freeFrom ? shipFree : shipPaid;

    if (pairs.length) {
      var lead = variantFor(pairs[0]);
      if (lead) {
        if (fallbackId) fallbackId.value = lead.id;
        if (gallery && gallery.wgShowMedia && lead.featured_media) {
          gallery.wgShowMedia(lead.featured_media);
        }
      }
    }

    syncSticky(qty, total, unit);
    if (errorBox) errorBox.classList.remove('is-visible');
  }

  /* ---------- events ---------- */

  root.addEventListener('change', function (event) {
    var target = event.target;

    if (target.name === 'wg-tier') {
      /* Carry the shopper's choices into the tier they just switched to. */
      pairsOf(target).forEach(function (pair) {
        controlsOf(pair).forEach(function (control) {
          writeControl(control, sticky[control.dataset.wgOpt]);
        });
      });
      var tierPicks = root.querySelector('[data-wg-picks="' + target.value + '"]');
      if (tierPicks && tierPicks.wgApplyUniform) {
        var t = tierPicks.querySelector('[data-wg-same]');
        tierPicks.wgApplyUniform(!t || t.checked);
      }
      refresh();
      return;
    }

    /* "Alle Paare gleich" toggle — its own listener has already mirrored /
       switched mode; just reprice. */
    if (target.matches('[data-wg-same]')) {
      refresh();
      return;
    }

    var control = target.closest('[data-wg-opt]');
    if (!control) return;
    sticky[control.dataset.wgOpt] = readControl(control);

    /* In uniform mode a change on pair 1 propagates to every pair. */
    var picks = control.closest('[data-wg-picks]');
    var row = control.closest('[data-wg-pair]');
    if (picks && picks.classList.contains('is-uniform') &&
        row === picks.querySelector('[data-wg-pair]') && picks.wgMirror) {
      picks.wgMirror();
    }

    refresh();
  });

  /* ---------- submit ---------- */

  function addToCart() {
    var tier = selectedTier();
    if (!tier) return;
    var pairs = pairsOf(tier);

    var merged = {};
    var incomplete = false;

    pairs.forEach(function (pair) {
      var variant = variantFor(pair);
      if (!variant || !variant.available) { incomplete = true; return; }
      merged[variant.id] = (merged[variant.id] || 0) + 1;
    });

    if (incomplete) {
      if (errorBox) {
        errorBox.textContent = 'Bitte wähle für jedes Paar eine verfügbare Farbe und Größe.';
        errorBox.classList.add('is-visible');
      }
      return;
    }

    var items = Object.keys(merged).map(function (id) {
      return { id: Number(id), quantity: merged[id] };
    });

    var label = submitBtn.querySelector('[data-wg-label]');
    var original = label.textContent;
    submitBtn.disabled = true;
    label.textContent = 'Wird hinzugefügt …';

    var rootUrl = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes.root : '/';

    /* Sections the cart drawer needs re-rendered — same set the theme's own
       product-form component asks for, so the drawer (free-shipping bar,
       item cards, totals) morphs to the new cart state instead of going stale. */
    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: sectionIds.join(',') })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart');
        return response.json();
      })
      .then(function (addResponse) {
        submitBtn.disabled = false;
        label.textContent = original;

        var drawer = document.querySelector('cart-drawer-component');

        if (!drawer || typeof drawer.open !== 'function') {
          window.location.href = rootUrl + 'cart';
          return;
        }

        fetch(rootUrl + 'cart.js')
          .then(function (response) { return response.json(); })
          .then(function (cart) {
            /* This is the theme's native "cart:update" event (same event
               ThemeEvents.cartUpdate / CartAddEvent use) — dispatching it
               plainly avoids depending on the module import map, since this
               script loads as a classic (non-module) script. */
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'wg-pdp',
                data: {
                  source: 'wg-pdp',
                  itemCount: cart.item_count,
                  sections: addResponse.sections
                }
              }
            }));
          })
          .finally(function () {
            drawer.open();
          });
      })
      .catch(function () {
        if (errorBox) {
          errorBox.textContent = 'Das hat nicht geklappt. Bitte versuche es noch einmal.';
          errorBox.classList.add('is-visible');
        }
        submitBtn.disabled = false;
        label.textContent = original;
      });
  }

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    addToCart();
  });

  /* ---------- sticky mobile bar ---------- */

  var stickyBar = document.querySelector('[data-wg-sticky]');
  var stickyQty = stickyBar && stickyBar.querySelector('[data-wg-sticky-qty]');
  var stickyTotal = stickyBar && stickyBar.querySelector('[data-wg-sticky-total]');
  var stickyUnit = stickyBar && stickyBar.querySelector('[data-wg-sticky-unit]');

  function syncSticky(qty, total, unit) {
    if (!stickyBar) return;
    if (stickyQty) stickyQty.textContent = qty + ' Paar';
    if (stickyTotal) stickyTotal.textContent = money(total);
    if (stickyUnit) stickyUnit.textContent = '· ' + money(unit) + ' pro Paar';
  }

  if (stickyBar) {
    stickyBar.hidden = false;

    var stickyBtn = stickyBar.querySelector('[data-wg-sticky-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        addToCart();
      });
    }

    /* Watch the button itself, not the whole buy box. The old build watched a
       700px container, so the bar was wrong at both ends of the scroll. */
    if ('IntersectionObserver' in window && submitBtn) {
      new IntersectionObserver(function (entries) {
        stickyBar.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { rootMargin: '0px 0px -80px 0px' }).observe(submitBtn);
    }
  }

  /* Stepz-swipe parity: default to a smaller size than whatever Shopify's
     own "first variant" happens to be, if a preference is configured. Runs
     before the initial refresh() so availability/pricing picks it up
     immediately, no synthetic events needed. */
  var defaultSize = root.dataset.defaultSize;
  if (defaultSize) {
    var hasMatch = false;
    root.querySelectorAll('.wg-swatch--pill input[type="radio"]').forEach(function (input) {
      if (input.value === defaultSize) hasMatch = true;
    });
    if (hasMatch) {
      root.querySelectorAll('.wg-swatch--pill input[type="radio"]').forEach(function (input) {
        input.checked = (input.value === defaultSize);
      });
    }
  }

  refresh();
})();

/* spec v3 §C13: footer <details> accordions start OPEN in the markup so
   they render fully on desktop and for no-JS; on phones we strip `open`
   so they start collapsed. Re-evaluated on resize across the breakpoint. */
(function () {
  'use strict';
  if (window.__wgFooterAccBound) return;   // already wired by wg-footer's own inline script
  window.__wgFooterAccBound = true;
  var accs = document.querySelectorAll('.wg-footer__acc');
  if (!accs.length) return;
  var mq = window.matchMedia('(max-width: 759px)');
  var userToggled = false;
  accs.forEach(function (d) {
    d.addEventListener('toggle', function () { userToggled = true; });
  });
  function sync() {
    if (userToggled) return;
    accs.forEach(function (d) { d.open = !mq.matches; });
  }
  sync();
  (mq.addEventListener ? mq.addEventListener('change', sync) : mq.addListener(sync));
})();

/* spec v5 §4.6: mirror the picked size into its field label ("Schuhgröße: 41-42"). */
(function () {
  'use strict';
  function label(input) {
    var field = input.closest('.wg-field--size');
    if (!field) return;
    var out = field.querySelector('[data-wg-sizename]');
    if (out) out.textContent = (input.value || '').replace(/^EU\s+/, '');
  }
  document.querySelectorAll('.wg-field--size .wg-swatch--pill input[type="radio"]').forEach(function (input) {
    input.addEventListener('change', function () { if (input.checked) label(input); });
    if (input.checked) label(input);
  });
})();
